// Full-screen overlays: start screen (with iPhone tips), sensor permission problems.

import { addTapHaptic } from '../input/haptics';

const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone =
  matchMedia('(display-mode: fullscreen)').matches ||
  matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

function iosTips(): string {
  if (!isIOS) return '';
  const tips = [];
  if (!isStandalone) tips.push('<b>Full screen:</b> tap Share → <b>Add to Home Screen</b> and play from the icon.');
  tips.push('<b>No flipping:</b> turn on <b>Portrait Orientation Lock</b> in Control Centre; the game stays landscape on its own.');
  return `<div class="tips">${tips.map((t) => `<p>${t}</p>`).join('')}</div>`;
}

export class Overlays {
  private readonly start: HTMLElement;
  private readonly message: HTMLElement;

  constructor(root: HTMLElement) {
    root.innerHTML = `
      <div class="overlay start">
        <div class="title">FONE <span>GP</span></div>
        <p class="hint">Hold your phone like a steering wheel, in landscape.<br/>Tilt to steer · hold right side to accelerate · hold left side to brake.</p>
        <div class="start-modes">
          <button class="start-btn">Time trial</button>
          <button class="race-btn">Race</button>
          <button class="challenge-btn">Challenges</button>
          <button class="wheel-btn">Steering wheel</button>
        </div>
        <p class="start-wheel"></p>
        <p class="start-best"></p>
        ${iosTips()}
        <p class="small">Uses your phone's motion sensors. Nothing leaves your device.</p>
      </div>
      <div class="overlay message"><div class="message-body"></div><button class="message-btn">OK</button></div>`;
    this.start = root.querySelector('.start') as HTMLElement;
    this.message = root.querySelector('.message') as HTMLElement;
    for (const sel of ['.start-btn', '.race-btn', '.challenge-btn', '.wheel-btn']) addTapHaptic(root.querySelector(sel) as HTMLElement);
    root.querySelector('.message-btn')!.addEventListener('click', () => this.message.classList.remove('show'));
    for (const el of root.querySelectorAll('.overlay')) el.addEventListener('pointerdown', (e) => e.stopPropagation());
  }

  onStart(handler: () => void): void {
    this.start.querySelector('.start-btn')!.addEventListener('click', handler);
  }

  onRace(handler: () => void): void {
    this.start.querySelector('.race-btn')!.addEventListener('click', handler);
  }

  onChallenges(handler: () => void): void {
    this.start.querySelector('.challenge-btn')!.addEventListener('click', handler);
  }

  onWheel(handler: () => void): void {
    this.start.querySelector('.wheel-btn')!.addEventListener('click', handler);
  }

  /** Wheel status line on the start screen (empty hides it). */
  setWheel(text: string): void {
    (this.start.querySelector('.start-wheel') as HTMLElement).textContent = text;
  }

  /** Line under the start button, e.g. the personal best. */
  setBest(text: string): void {
    (this.start.querySelector('.start-best') as HTMLElement).textContent = text;
  }

  showStart(show: boolean): void {
    this.start.classList.toggle('show', show);
  }

  showMessage(html: string): void {
    (this.message.querySelector('.message-body') as HTMLElement).innerHTML = html;
    this.message.classList.add('show');
  }
}

export const PERMISSION_DENIED_HTML = `
  <h2>Motion access is off</h2>
  <p>Fone GP steers with your phone's tilt, so it needs motion &amp; orientation access.</p>
  <p><b>iPhone:</b> iOS won't ask again in this tab. Close the tab, open the game again and tap <b>Allow</b>.
  If it still doesn't ask: Settings → Apps → Safari → Advanced → Website Data, remove this site, then reload.</p>`;

export const NO_SENSOR_HTML = `
  <h2>No motion sensor found</h2>
  <p>This device isn't sending tilt data, so it can't steer. Try a phone with a gyroscope.</p>
  <p>On a computer you can drive with the keyboard: arrows or WASD, <b>C</b> re-centres, <b>H</b> switches horizon mode.</p>`;

export const CREDITS_HTML = `
  <h2>Credits</h2>
  <p style="font-size:13px;text-align:left">
  <b>Car</b>: based on <a href="https://sketchfab.com/3d-models/full-f1-2022-bcacdcc58e224a949be6f3c4b6d400b3" target="_blank" rel="noopener">"Full F1 2022"</a> by <a href="https://sketchfab.com/The_3D_Dude" target="_blank" rel="noopener">The 3D Dude</a>, licensed under <a href="http://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a>. Modified: rescaled to 2026 dimensions, wheels separated, new livery, optimised.<br/><br/>
  <b>Track layout</b>: TUMFTM racetrack-database (TU Munich, LGPL-3.0) and OpenStreetMap data © OpenStreetMap contributors (ODbL).<br/><br/>
  <b>Track elevation</b>: produced using Copernicus WorldDEM-30 © DLR e.V. 2010-2014 and © Airbus Defence and Space GmbH 2014-2018 provided under COPERNICUS by the European Union and ESA; all rights reserved.<br/><br/>
  Fone GP is a fan project, not affiliated with Formula 1, the FIA or Sepang International Circuit.</p>`;
